import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Plus, FileText, Clock, CheckCircle, XCircle, AlertTriangle,
    LogOut, User, Calendar, Activity, TrendingUp, Filter, Search,
    Bell, BookOpen, BarChart3, Settings, Building, Shield, Sun, Moon, ScanLine
} from 'lucide-react';
import NotificationSettings from '../components/common/NotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/logo.png';

// Helper function to calculate age from DOB
const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
};

const urgencyColors = {
    urgent: 'badge-urgent',
    semi_urgent: 'badge-moderate',
    moderate: 'badge-moderate', // Keep for backward compatibility
    routine: 'badge-routine',
    screening_rejected: 'badge-rejected'
};

const statusColors = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    accepted: 'badge-accepted',
    rejected: 'badge-rejected',
    screening_rejected: 'badge-rejected',
    scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
    completed: 'bg-slate-100 text-slate-800 border-slate-200'
};

const conditionLabels = {
    hip_arthritis: 'Hip Osteoarthritis',
    knee_arthritis: 'Knee Osteoarthritis',
    shoulder_arthritis: 'Shoulder Osteoarthritis',
    rotator_cuff_tear: 'Rotator Cuff Tear',
    impingement_syndrome: 'Impingement Syndrome',
    calcific_tendinitis: 'Calcific Tendinitis',
    ankle_arthritis: 'Ankle Osteoarthritis',
    ankle_impingement: 'Ankle Impingement',
    hallux_valgus: 'Hallux Valgus',
    spinal_stenosis: 'Spinal Stenosis',
    disc_herniation: 'Disc Herniation',
    carpal_tunnel_syndrome: 'Carpal Tunnel Syndrome',
    trigger_finger: 'Trigger Finger',
    dupuytrens_contracture: "Dupuytren's Contracture",
    base_thumb_arthritis: 'Base of Thumb Arthritis',
    tennis_elbow: 'Tennis Elbow',
    golfers_elbow: "Golfer's Elbow",
    ulnar_nerve_compression: 'Ulnar Nerve Compression'
};

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, logout, isConsultant } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [referrals, setReferrals] = useState([]);
    const [imagingFlags, setImagingFlags] = useState({});
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [urgencyFilter, setUrgencyFilter] = useState('');
    const [conditionFilter, setConditionFilter] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Redirect super_admin to admin portal - they shouldn't see patient data
    // Redirect consultant to consultant dashboard
    useEffect(() => {
        if (user?.role === 'super_admin') {
            navigate('/admin');
        } else if (user?.role === 'consultant') {
            navigate('/consultant');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (user?.role !== 'super_admin') {
            fetchData();
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, urgencyFilter, conditionFilter, user]);

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            if (searchQuery) params.append('search', searchQuery);
            if (urgencyFilter && urgencyFilter !== 'all') params.append('urgency_level', urgencyFilter);
            if (conditionFilter && conditionFilter !== 'all') params.append('condition', conditionFilter);
            
            const [referralsRes, statsRes] = await Promise.all([
                axios.get(`${API}/referrals?${params.toString()}`),
                axios.get(`${API}/statistics`)
            ]);
            setReferrals(referralsRes.data);
            setStatistics(statsRes.data);
            fetchImagingFlags(referralsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const fetchImagingFlags = async (refs) => {
        try {
            const ids = (refs || []).map((r) => r.id);
            if (ids.length === 0) return;
            const res = await axios.post(`${API}/nir/imaging-flags`, { referral_ids: ids });
            setImagingFlags(res.data?.flags || {});
        } catch (_) { /* non-fatal background enrichment */ }
    };

    const fetchNotifications = async () => {
        try {
            const response = await axios.get(`${API}/notifications`);
            setNotifications(response.data.notifications || []);
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Failed to fetch notifications');
        }
    };

    const markAllRead = async () => {
        try {
            await axios.put(`${API}/notifications/read-all`);
            setUnreadCount(0);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark notifications as read');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const filteredReferrals = referrals.filter(r => {
        if (activeTab === 'all') return true;
        if (activeTab === 'pending') return r.status === 'pending';
        if (activeTab === 'accepted') return r.status === 'accepted';
        if (activeTab === 'rejected') return r.status === 'rejected' || r.status === 'screening_rejected';
        return true;
    });

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const canAccessAnalytics = ['institution_admin', 'nhs_manager', 'super_admin'].includes(user?.role);
    const canAccessAdmin = user?.role === 'super_admin';

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <img src={LOGO_URL} alt="FendanaCor" className="w-10 h-10 rounded-lg shadow-sm" />
                            <div>
                                <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    FendanaCor
                                </h1>
                                <p className="text-xs text-muted-foreground">Ortho referrals without pain</p>
                            </div>
                        </div>
                        
                        {/* Navigation */}
                        <nav className="hidden md:flex items-center gap-1">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/dashboard')}
                                className="text-primary"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Referrals
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => navigate('/guidelines')}
                            >
                                <BookOpen className="w-4 h-4 mr-2" />
                                Guidelines
                            </Button>
                            {canAccessAnalytics && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigate('/analytics')}
                                >
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    Analytics
                                </Button>
                            )}
                            {isConsultant && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigate('/consultant/calendar')}
                                >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Calendar
                                </Button>
                            )}
                            {canAccessAdmin && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => navigate('/admin')}
                                >
                                    <Shield className="w-4 h-4 mr-2" />
                                    Admin
                                </Button>
                            )}
                        </nav>

                        <div className="flex items-center gap-4">
                            {/* Settings */}
                            <Dialog open={showSettings} onOpenChange={setShowSettings}>
                                <DialogTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid="settings-btn"
                                        title="Settings"
                                    >
                                        <Settings className="w-5 h-5" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-lg">
                                    <DialogHeader>
                                        <DialogTitle>Settings</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                        <NotificationSettings />
                                    </div>
                                </DialogContent>
                            </Dialog>

                            {/* Dark Mode Toggle */}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={toggleTheme}
                                data-testid="theme-toggle-btn"
                                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            >
                                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </Button>

                            {/* Notifications */}
                            <div className="relative">
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    className="relative"
                                    data-testid="notifications-btn"
                                >
                                    <Bell className="w-5 h-5" />
                                    {unreadCount > 0 && (
                                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                                            {unreadCount}
                                        </span>
                                    )}
                                </Button>
                                
                                {showNotifications && (
                                    <div className="absolute right-0 mt-2 w-80 bg-card rounded-xl shadow-xl border border-border z-50">
                                        <div className="p-4 border-b border-border flex justify-between items-center">
                                            <h3 className="font-semibold text-foreground">Notifications</h3>
                                            <Button variant="ghost" size="sm" onClick={markAllRead}>
                                                Mark all read
                                            </Button>
                                        </div>
                                        <div className="max-h-96 overflow-y-auto">
                                            {notifications.length === 0 ? (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    No notifications
                                                </div>
                                            ) : (
                                                notifications.slice(0, 10).map((notif) => (
                                                    <div 
                                                        key={notif.id}
                                                        className={`p-4 border-b border-border hover:bg-muted cursor-pointer ${
                                                            !notif.is_read ? 'bg-primary/5' : ''
                                                        }`}
                                                        onClick={() => notif.referral_id && navigate(`/referral/${notif.referral_id}`)}
                                                    >
                                                        <p className="text-sm font-medium text-foreground">{notif.title}</p>
                                                        <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <Link
                                to="/profile"
                                className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground hover:text-teal-700 hover:bg-teal-50 rounded-md px-2 py-1 transition-colors"
                                data-testid="profile-link"
                                title="Profile & Security"
                            >
                                <User className="w-4 h-4" />
                                <span>{user?.full_name}</span>
                                <Badge variant="outline" className="ml-2 capitalize">
                                    {user?.role?.replace('_', ' ')}
                                </Badge>
                            </Link>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/profile')}
                                data-testid="profile-btn"
                                className="sm:hidden text-muted-foreground"
                                title="Profile & Security"
                            >
                                <User className="w-5 h-5" />
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={handleLogout}
                                data-testid="logout-btn"
                                className="text-slate-600 hover:text-slate-900"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Overview */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
                >
                    <Card className="card-clinical border-t-4 border-t-teal-600">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Referrals</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {statistics?.total_referrals || 0}
                                    </p>
                                </div>
                                <div className="p-3 bg-teal-50 rounded-lg">
                                    <FileText className="w-6 h-6 text-teal-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-clinical border-t-4 border-t-amber-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Pending Review</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {statistics?.by_status?.pending || 0}
                                    </p>
                                </div>
                                <div className="p-3 bg-amber-50 rounded-lg">
                                    <Clock className="w-6 h-6 text-amber-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-clinical border-t-4 border-t-emerald-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Accepted</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {statistics?.by_status?.accepted || 0}
                                    </p>
                                </div>
                                <div className="p-3 bg-emerald-50 rounded-lg">
                                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-clinical border-t-4 border-t-rose-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Urgent Cases</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {statistics?.by_urgency?.urgent || 0}
                                    </p>
                                </div>
                                <div className="p-3 bg-rose-50 rounded-lg">
                                    <AlertTriangle className="w-6 h-6 text-rose-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Search and Filters */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-6"
                >
                    <Card className="card-clinical">
                        <CardContent className="pt-6">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        placeholder="Search by patient name or NHS number..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10 input-clinical"
                                        data-testid="search-input"
                                    />
                                </div>
                                <Select value={urgencyFilter || undefined} onValueChange={setUrgencyFilter}>
                                    <SelectTrigger className="w-full md:w-48" data-testid="urgency-filter">
                                        <SelectValue placeholder="All Urgencies" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Urgencies</SelectItem>
                                        <SelectItem value="urgent">Urgent</SelectItem>
                                        <SelectItem value="semi_urgent">Semi-urgent</SelectItem>
                                        <SelectItem value="routine">Routine</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={conditionFilter || undefined} onValueChange={setConditionFilter}>
                                    <SelectTrigger className="w-full md:w-48" data-testid="condition-filter">
                                        <SelectValue placeholder="All Conditions" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Conditions</SelectItem>
                                        <SelectItem value="hip_arthritis">Hip Arthritis</SelectItem>
                                        <SelectItem value="knee_arthritis">Knee Arthritis</SelectItem>
                                        <SelectItem value="shoulder_arthritis">Shoulder Arthritis</SelectItem>
                                        <SelectItem value="rotator_cuff_tear">Rotator Cuff Tear</SelectItem>
                                        <SelectItem value="carpal_tunnel_syndrome">Carpal Tunnel</SelectItem>
                                        <SelectItem value="spinal_stenosis">Spinal Stenosis</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Action Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6"
                >
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isConsultant ? 'Referral Queue' : 'My Referrals'}
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">
                            {isConsultant 
                                ? 'Review and triage incoming referrals' 
                                : 'Track your submitted referrals'}
                        </p>
                    </div>
                    {(user?.role === 'doctor' || user?.role === 'primary_care') && (
                        <Button 
                            onClick={() => navigate('/new-referral')}
                            className="btn-primary"
                            data-testid="new-referral-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Referral
                        </Button>
                    )}
                </motion.div>

                {/* Referrals List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card className="card-clinical">
                        <CardHeader className="border-b border-slate-100 pb-4">
                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <TabsList className="bg-slate-100">
                                    <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                                    <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
                                    <TabsTrigger value="accepted" data-testid="tab-accepted">Accepted</TabsTrigger>
                                    <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </CardHeader>
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="p-12 text-center text-slate-500">
                                    <Activity className="w-8 h-8 animate-spin mx-auto mb-4" />
                                    <p>Loading referrals...</p>
                                </div>
                            ) : filteredReferrals.length === 0 ? (
                                <div className="p-12 text-center">
                                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-slate-900 mb-2">No referrals found</h3>
                                    <p className="text-slate-500 text-sm mb-4">
                                        {(user?.role === 'doctor' || user?.role === 'primary_care') && "Create your first referral to get started"}
                                    </p>
                                    {(user?.role === 'doctor' || user?.role === 'primary_care') && (
                                        <Button 
                                            onClick={() => navigate('/new-referral')}
                                            className="btn-primary"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            New Referral
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {filteredReferrals.map((referral, index) => (
                                        <motion.div
                                            key={referral.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="p-6 hover:bg-slate-50/50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/referral/${referral.id}`)}
                                            data-testid={`referral-item-${referral.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h4 className="text-lg font-semibold text-slate-900">
                                                            {referral.patient.first_name} {referral.patient.last_name}
                                                            {referral.patient.date_of_birth && (
                                                                <span className="ml-2 text-sm text-slate-500 font-normal">
                                                                    ({referral.patient_age || calculateAge(referral.patient.date_of_birth)} yrs)
                                                                </span>
                                                            )}
                                                        </h4>
                                                        <Badge className={urgencyColors[referral.urgency_level]}>
                                                            {referral.urgency_level.replace('_', ' ')}
                                                        </Badge>
                                                        <Badge className={statusColors[referral.status]}>
                                                            {referral.status.replace('_', ' ')}
                                                        </Badge>
                                                        {imagingFlags[referral.id] > 0 && (
                                                            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200" data-testid={`imaging-flag-${referral.id}`}>
                                                                <ScanLine className="w-3 h-3 mr-1" />
                                                                Imaging on file
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-slate-600 text-sm mb-2">
                                                        {conditionLabels[referral.condition] || referral.condition}
                                                        <span className="text-slate-400 mx-2">•</span>
                                                        {referral.body_part.charAt(0).toUpperCase() + referral.body_part.slice(1)}
                                                        <span className="text-slate-400 mx-2">•</span>
                                                        BMI: {referral.bmi}
                                                    </p>
                                                    <div className="flex items-center gap-4 text-xs text-slate-500">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(referral.created_at)}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <TrendingUp className="w-3 h-3" />
                                                            Score: {referral.urgency_score}/100
                                                        </span>
                                                        {!referral.screening_passed && (
                                                            <span className="flex items-center gap-1 text-rose-600">
                                                                <XCircle className="w-3 h-3" />
                                                                Screening criteria not met
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    {referral.target_institution_name && (
                                                        <p className="text-xs text-slate-500 flex items-center gap-1">
                                                            <Building className="w-3 h-3" />
                                                            {referral.target_institution_name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
}
